import React, { useState } from 'react';

import classnames from 'classnames';

import { removeActiveLayer, setActiveLayerVisible } from '../../redux/active_layers/Actions';
import { useDispatch, useSelector } from 'react-redux';
import type { StoreState } from '../../redux/Store';

import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core';

import { Typography, IconButton, Collapse } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import DeleteIcon from '@material-ui/icons/CloseOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

import LegendCircleGroup from '../Common/Legend/CircleGroup';
import LegendCircleRow from '../Common/Legend/CircleRow';
import LegendFillGroup from '../Common/Legend/FillGroup';
import LegendFillRow from '../Common/Legend/FillRow';
import LegendLineGroup from '../Common/Legend/LineGroup';
import LegendLineRow from '../Common/Legend/LineRow';
import LegendCircular from '../Common/Legend/Circular';
import LegendImg from '../Common/Legend/Img';
import LegendRaster from '../Common/Legend/Raster';
import LegendRangeDivided from '../Common/Legend/RangeDivided';
import LegendComposite from '../Common/Legend/Composite';

import {
    ILegendItem,
    LegendSpecificationType,
    IFillRowStyles,
    IFillsGroup,
    ICircle,
    ICirclesGroup,
    ILineGroup,
    ILine,
    IImg,
    IRaster,
    IRangeDivided,
    IComposite
} from '../../redux/layers/layerTypes';

type LegendItemProps = {
    layer: ILegendItem;
    isStatic?: boolean;
};

function LegendItem({ layer, isStatic = false }: LegendItemProps) {
    let legendSpec: any[] = [];
    layer.styles && layer.styles.length > 0
        ? (legendSpec = layer.styles[layer.currentStyleIdx || 0].legendSpecification)
        : null;

    const classes = useStyles();
    const dispatch = useDispatch();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const dispatchSetActiveLayerVisible = (uid: string, visible: boolean) => {
        dispatch(setActiveLayerVisible(uid, visible));
    };
    const dispatchRemoveActiveLayer = (uid: string) => dispatch(removeActiveLayer(uid));

    function convertLegendStylingToJSX(style: LegendSpecificationType, key: number) {
        switch (style.type) {
            case 'fill-group':
                return <LegendFillGroup key={key} style={style as IFillsGroup} />;
            case 'fill-row':
            case 'fill':
                return <LegendFillRow key={key} style={style as IFillRowStyles} />;
            case 'circle-row':
            case 'circle':
                return <LegendCircleRow key={key} style={style as ICircle} />;
            case 'circle-group':
                return <LegendCircleGroup key={key} style={style as ICirclesGroup} />;
            case 'circular':
                return <LegendCircular key={key} style={style as ICirclesGroup} />;
            case 'line-group':
                return <LegendLineGroup key={key} style={style as ILineGroup} />;
            case 'line-row':
            case 'line':
                return <LegendLineRow key={key} style={style as ILine} />;
            case 'img':
                return <LegendImg key={key} style={style as IImg} />;
            case 'raster':
                return <LegendRaster key={key} style={style as IRaster} />;
            case 'range-divided':
                return <LegendRangeDivided key={key} style={style as IRangeDivided} />;
            case 'composite':
                return <LegendComposite key={key} style={style as IComposite} />;
            default:
                console.log('Unidentified type', style.type);
        }
    }

    const isLegendStyling = legendSpec && legendSpec.length > 0;
    // Collapsible if more than 1 styling or is there is 1 styling but it is a complex type
    const isCollapsible =
        isLegendStyling &&
        (legendSpec.length > 1 ||
            (legendSpec.length === 1 &&
                ['range-divided', 'circular', 'circle-group', 'fill-group', 'line-group'].includes(
                    legendSpec[0].type
                )));

    return (
        <div>
            <div className={classes.row}>
                {isCollapsible && !isStatic && (
                    <IconButton
                        className={classes.drowdownButton}
                        size="small"
                        onClick={e => setIsCollapsed(!isCollapsed)}>
                        <ArrowDropDownIcon
                            className={classnames(
                                classes.icon,
                                isCollapsed && classes.iconMinimised
                            )}
                        />
                    </IconButton>
                )}
                {isLegendStyling && !isCollapsible ? (
                    <div>{legendSpec.map((color, j) => convertLegendStylingToJSX(color, j))}</div>
                ) : (
                    <Typography
                        className={classnames(classes.name, isCollapsible && classes.bold)}
                        variant="caption"
                        color="textPrimary">
                        {layer.name}
                    </Typography>
                )}
                {!isStatic && (
                    <>
                        <IconButton
                            className={classes.autoMarginLeft}
                            size="small"
                            onClick={e => dispatchSetActiveLayerVisible(layer.uid, !layer.visible)}>
                            {layer.visible ? (
                                <VisibilityIcon className={classes.icon} />
                            ) : (
                                <VisibilityOffIcon className={classes.icon} color="disabled" />
                            )}
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={e => dispatchRemoveActiveLayer(layer.uid)}>
                            <DeleteIcon className={classes.icon} color="disabled" />
                        </IconButton>
                    </>
                )}
            </div>
            {isCollapsible && (
                <Collapse
                    in={!isCollapsed}
                    classes={{
                        wrapperInner: classes.collapseWrapperInner,
                        entered: classes.collapseEntered
                    }}>
                    {legendSpec.map((color, j) => convertLegendStylingToJSX(color, j))}
                </Collapse>
            )}
        </div>
    );
}

// Static functions
LegendItem.isCollapsible = (legendSpec: LegendSpecificationType[]) => {
    const isLegendStyling = legendSpec && legendSpec.length > 0;
    return Boolean(
        isLegendStyling &&
            (legendSpec.length > 1 ||
                (legendSpec.length === 1 &&
                    [
                        'range-divided',
                        'circular',
                        'circle-group',
                        'fill-group',
                        'line-group'
                    ].includes(legendSpec[0].type)))
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px'
        },
        drowdownButton: {
            marginLeft: -6.25
        },
        name: {
            marginTop: 2
        },
        bold: {
            fontWeight: 500
        },
        autoMarginLeft: {
            marginLeft: 'auto'
        },
        icon: {
            transition: '0.5s',
            width: '0.75em',
            height: '0.75em'
        },
        iconMinimised: {
            transform: 'rotate(-90deg)'
        },
        collapseWrapperInner: {
            padding: '0 10px',
            '&> *:not(:last-child)': {
                marginBottom: 10
            }
        },
        collapseEntered: {
            marginBottom: 10
        }
    })
);

export default LegendItem;
